import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment.local';

const TOKEN_KEY = 'ss_token';
const API_BASE = environment.apiBaseUrl.replace(/\/$/, '');

export interface CurrentUser {
  readonly email: string;
  readonly username: string;
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly fullName: string | null;
  readonly phone: string | null;
}

interface LoginDto {
  username: string;
  password: string;
}

interface RegisterDto {
  email: string;
  username: string;
  password: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  frontendUrl: string | null;
}

interface ActivateDto {
  email: string;
  code: string;
}

interface RecoverEmailDto {
  email: string;
  frontendUrl: string | null;
}

interface ResendActivationDto {
  email: string;
  frontendUrl: string | null;
}

interface RecoverCodeDto {
  email: string;
  code: string;
}

interface RecoverPasswordDto {
  email: string;
  password: string;
}

interface TokenResponseShape {
  accessToken?: string;
  access_token?: string;
  token?: string;
  jwt?: string;
  bearer?: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  private readonly _token = signal<string | null>(readStoredToken());
  private readonly _user = signal<CurrentUser | null>(null);

  readonly token = this._token.asReadonly();
  readonly user = this._user.asReadonly();
  readonly isAuthenticated = computed(() => !!this._token());

  constructor() {
    if (this._token()) {
      this.loadMe().subscribe();
    }
  }

  login(username: string, password: string, remember = true): Observable<void> {
    const body: LoginDto = { username, password };
    return this.http
      .post<TokenResponseShape | string>(`${API_BASE}/api/auth`, body, {
        withCredentials: true,
      })
      .pipe(
        map((res) => extractToken(res)),
        tap((token) => {
          if (!token) {
            throw new Error('Răspuns neașteptat de la server.');
          }
          this.persistToken(token, remember);
          this._token.set(token);
        }),
        map(() => void 0),
        tap(() => this.loadMe().subscribe()),
      );
  }

  register(
    email: string,
    username: string,
    password: string,
    firstName: string,
    lastName: string,
    phone?: string | null,
  ): Observable<void> {
    const body: RegisterDto = {
      email,
      username,
      password,
      firstName,
      lastName,
      phone: phone?.trim() || null,
      frontendUrl: buildFrontendUrl(),
    };
    return this.http.post<unknown>(`${API_BASE}/api/accounts`, body).pipe(map(() => void 0));
  }

  activate(email: string, code: string): Observable<void> {
    const body: ActivateDto = { email, code };
    return this.http.post<unknown>(`${API_BASE}/api/auth/activate`, body).pipe(map(() => void 0));
  }

  resendActivation(email: string): Observable<void> {
    const body: ResendActivationDto = { email, frontendUrl: buildFrontendUrl() };
    return this.http
      .post<unknown>(`${API_BASE}/api/auth/activate/resend`, body)
      .pipe(map(() => void 0));
  }

  requestPasswordRecovery(email: string): Observable<void> {
    const body: RecoverEmailDto = { email, frontendUrl: buildFrontendUrl() };
    return this.http
      .post<unknown>(`${API_BASE}/api/auth/recover/email`, body)
      .pipe(map(() => void 0));
  }

  verifyRecoveryCode(email: string, code: string): Observable<void> {
    const body: RecoverCodeDto = { email, code };
    return this.http
      .post<unknown>(`${API_BASE}/api/auth/recover/code`, body)
      .pipe(map(() => void 0));
  }

  resetPassword(email: string, password: string): Observable<void> {
    const body: RecoverPasswordDto = { email, password };
    return this.http
      .post<unknown>(`${API_BASE}/api/auth/recover/password`, body)
      .pipe(map(() => void 0));
  }

  loadMe(): Observable<CurrentUser | null> {
    return this.http.get<CurrentUser>(`${API_BASE}/api/accounts/me`).pipe(
      tap((u) => this._user.set(u)),
      catchError((err) => {
        if (err?.status === 401) {
          this.clearSession();
        }
        return of(null);
      }),
    );
  }

  logout(): void {
    this.clearSession();
    this.router.navigateByUrl('/');
  }

  private persistToken(token: string, remember: boolean): void {
    try {
      if (remember) {
        localStorage.setItem(TOKEN_KEY, token);
        sessionStorage.removeItem(TOKEN_KEY);
      } else {
        sessionStorage.setItem(TOKEN_KEY, token);
        localStorage.removeItem(TOKEN_KEY);
      }
    } catch {
      // storage disabled — signal still holds token for the session
    }
  }

  private clearSession(): void {
    this._token.set(null);
    this._user.set(null);
    try {
      localStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(TOKEN_KEY);
    } catch {
      // noop
    }
  }
}

function readStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY) ?? sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function buildFrontendUrl(): string | null {
  if (typeof window === 'undefined') return null;
  return window.location.origin;
}

function extractToken(res: TokenResponseShape | string | null | undefined): string | null {
  if (!res) return null;
  if (typeof res === 'string') return res.trim() || null;
  return (
    res.accessToken ??
    res.access_token ??
    res.token ??
    res.jwt ??
    res.bearer ??
    null
  );
}
